import { useState, useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { Modal, Button, Radio } from 'antd';
import Cropper from 'react-easy-crop';
import { UploadIcon } from 'src/icons';
import { showToastError } from 'src/utilities/errortoast';

const getCroppedImg = (imageSrc, pixelCrop) => {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/jpeg',
        0.9
      );
    };
    image.src = imageSrc;
  });
};

const UploadImages = ({
  updateLogoUrl,
  selectedFile,
  setSelectedFile,
  handleBlobSelect,
  isEditPage,
  setOrgData,
  orgData,
  logoUrl,
  fromAddPage,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [urlSubmitted, setUrlSubmitted] = useState(false);

  // Crop modal states
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(1);

  const fileInputRef = useRef(null);
  const urlInputRef = useRef(null);

  useEffect(() => {
    if (isEditPage && orgData?.logo_url) {
      setShowUrlInput(true);
    }
  }, [isEditPage, orgData?.logo_url]);

  useEffect(() => {
    if (logoUrl) {
      setImageUrl(logoUrl);
      setUrlSubmitted(true);
    }
  }, [logoUrl]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = [...e.dataTransfer.files];
    handleFileSelect(files[0]);
  };

  const handleFileInputChange = (e) => {
    const files = [...e.target.files];
    handleFileSelect(files[0]);
  };

  const handleFileSelect = (file) => {
    if (file) {
      const validTypes = ['image/png', 'image/jpeg'];
      const maxSize = 5 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        showToastError('Only PNG and JPEG images are allowed.');
        return;
      }

      if (file.size > maxSize) {
        showToastError('File size should be maximum 5MB.');
        return;
      }

      // Check image dimensions before opening crop modal
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;

        // Get target dimensions based on current aspect ratio
        const targetWidth = 300;
        const targetHeight = 200;

        // Check if image is smaller than target dimensions
        if (width < targetWidth || height < targetHeight) {
          showToastError(
            `Uploaded image is too small. Image should be ${targetWidth} * ${targetHeight} or larger.`
          );
          return;
        }

        // Open crop modal
        const url = URL.createObjectURL(file);
        setImageToCrop(url);
        setCropModalOpen(true);
      };

      img.onerror = () => {
        showToastError('Error loading image. Please try again.');
      };

      img.src = URL.createObjectURL(file);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropFinish = async () => {
    try {
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);

      // Create file extension from original image
      const ext = imageToCrop.includes('data:') ? 'jpg' : 'jpg';
      croppedBlob.ext = ext;

      // Pass the cropped blob to parent
      handleBlobSelect(croppedBlob);

      // Clean up
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
      setCropModalOpen(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      showToastError('Error cropping image. Please try again.');
    }
  };

  const handleCropCancel = () => {
    URL.revokeObjectURL(imageToCrop);
    setImageToCrop(null);
    setCropModalOpen(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleChangeImageClick = () => {
    setOrgData((prevOrgData) => ({
      ...prevOrgData,
      logo_url: null,
    }));
    fileInputRef.current.click();
  };

  const handleDelete = () => {
    setSelectedFile(null);
    setOrgData((prevOrgData) => ({
      ...prevOrgData,
      logo_url: null,
    }));
    fileInputRef.current.value = null;
  };

  const handleUploadAsUrlClick = () => {
    setShowUrlInput(true);
  };

  const handleUrlSubmit = () => {
    if (urlInputRef.current) {
      const enteredUrl = urlInputRef.current.value.trim();
      const validUrlExtensions = /\.(jpeg|jpg|png)$/i;
      if (enteredUrl === '') {
        showToastError('Please enter a URL.');
      } else if (!validUrlExtensions.test(enteredUrl)) {
        showToastError('Only PNG and JPEG images are allowed.');
      } else {
        setImageUrl(enteredUrl);
        setUrlSubmitted(true);
        if (updateLogoUrl) {
          updateLogoUrl(enteredUrl);
        }
      }
    }
  };

  const handleRemoveUrl = () => {
    updateLogoUrl(null);
    setImageUrl('');
    if (urlInputRef.current) {
      urlInputRef.current.value = '';
    }
    if (updateLogoUrl) {
      updateLogoUrl(null);
    }
    setUrlSubmitted(false);
    setShowUrlInput(false);
  };

  useEffect(() => {
    const handlePaste = (event) => {
      const pastedText = (event.clipboardData || window.clipboardData).getData(
        'text'
      );
      const validUrlExtensions = /\.(jpeg|jpg|png)$/i;
      if (
        pastedText.startsWith('http') &&
        validUrlExtensions.test(pastedText)
      ) {
        setImageUrl(pastedText);
        setUrlSubmitted(true);
        if (updateLogoUrl) {
          updateLogoUrl(pastedText);
        }
      } else {
        showToastError('Only PNG and JPEG image URLs are allowed.');
      }
    };

    if (urlInputRef.current) {
      urlInputRef.current.addEventListener('paste', handlePaste);
    }

    return () => {
      if (urlInputRef.current) {
        urlInputRef.current.removeEventListener('paste', handlePaste);
      }
    };
  }, [urlInputRef, updateLogoUrl]);

  const handleURLentry = (e) => {
    fromAddPage &&
      setOrgData((prevOrgData) => ({
        ...prevOrgData,
        logo_url: null,
      }));
    setImageUrl(e.target.value);
  };

  const handleRemoveOrCancel = () => {
    if (urlSubmitted || logoUrl) {
      handleRemoveUrl();
    } else {
      setImageUrl('');
      setShowUrlInput(false);
    }
  };

  const handleAspectRatioChange = (ratio) => {
    setAspectRatio(ratio);
  };

  return (
    <>
      <div
        className={classNames(
          'h-[36rem] text-center bg-[#00000005] border-2 border-[#00d090] border-dashed rounded-lg p-6 relative',
          {
            'border-blue-500 bg-blue-100': isDragging,
          }
        )}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileInputChange}
          accept="image/png, image/jpeg"
        />
        {selectedFile || (orgData?.logo_url && fromAddPage) ? (
          <>
            <img
              src={
                selectedFile
                  ? URL.createObjectURL(selectedFile)
                  : orgData?.logo_url
              }
              alt="Selected"
              className="mx-auto mb-4"
              style={{
                maxWidth: '400px',
                maxHeight: '150px',
                objectFit: 'contain',
              }}
            />
            <div className="flex justify-center">
              <button
                className="bg-[#00d090] text-white py-2 px-4 rounded mr-4"
                onClick={handleChangeImageClick}
              >
                Change Image
              </button>
              <button
                className="bg-red-500 text-white py-2 px-4 rounded"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </>
        ) : showUrlInput ? (
          <div className="flex flex-col items-center">
            {imageUrl && (
              <img
                src={imageUrl}
                className="mb-4"
                style={{
                  maxWidth: '400px',
                  maxHeight: '150px',
                  objectFit: 'contain',
                }}
              />
            )}
            <input
              ref={urlInputRef}
              value={imageUrl}
              onChange={(e) => handleURLentry(e)}
              type="text"
              className="w-72 p-3 outline-1 outline-[#E5E8EB] font-sans h-15 flex items-center rounded-xl outline-none"
              placeholder="Enter image URL"
              readOnly={urlSubmitted}
            />
            <div className="flex justify-center">
              {!urlSubmitted && (
                <button
                  className="bg-[#00d090] text-white py-2 px-4 rounded mt-5 mr-4"
                  onClick={handleUrlSubmit}
                >
                  Submit
                </button>
              )}
              <button
                className="bg-red-500 text-white py-2 px-4 rounded mt-5"
                onClick={handleRemoveOrCancel}
              >
                {urlSubmitted ? 'Remove' : 'Cancel'}
              </button>
            </div>
          </div>
        ) : (
          <label className="text-lg font-semibold text-center cursor-pointer">
            <div className="h-full flex flex-col justify-center items-center">
              <UploadIcon />
              <span className="mt-4">
                Click or drag file to this area to upload
              </span>
              <ul className="flex flex-col items-start text-xs text-gray-500 mt-3 list-disc list-inside">
                <li>The image upload should be in JPEG/PNG format.</li>
                <li>
                  The image will be cropped to 300×200 or 300×300 px (W×H).
                </li>
              </ul>
              <button
                className="bg-[#00d090] text-white py-2 px-4 rounded-lg mt-4"
                onClick={handleUploadButtonClick}
              >
                Upload Image
              </button>
              <button
                className="bg-[#00d090] text-white py-2 px-4 rounded-lg mt-4"
                onClick={handleUploadAsUrlClick}
              >
                Upload as URL
              </button>
            </div>
          </label>
        )}
      </div>

      {/* Crop Modal */}
      <Modal
        title="Crop Image"
        open={cropModalOpen}
        onCancel={handleCropCancel}
        footer={[
          <Button key="cancel" onClick={handleCropCancel}>
            Cancel
          </Button>,
          <Button key="finish" type="primary" onClick={handleCropFinish}>
            Finish
          </Button>,
        ]}
        width={600}
      >
        <div className="mb-4 flex items-center">
          <div>
            <span className="mr-2">Aspect Ratio:</span>
          </div>
          <div className="px-1">
            <Radio.Group
              value={aspectRatio}
              onChange={(e) => {
                handleAspectRatioChange(e.target.value);
              }}
              className="flex justify-between items-center cursor-pointer"
            >
              <Radio.Button className="" key={1} value={1}>
                Square (300px*300px)
              </Radio.Button>

              <Radio.Button className="" key={3 / 2} value={3 / 2}>
                Landscape (300px*200px)
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>

        <div
          className="relative bg-[#f0f0f0]"
          style={{
            maxHeight: '500px',
            minHeight: '150px',
          }}
        >
          {imageToCrop && (
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default UploadImages;
